import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Building, Hash, AlertTriangle, ArrowLeft, Search, Filter, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { carrierApi } from '../services/api';
import { VINDetailModal } from '../components/VINDetailModal';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { EmptyState } from '../components/shared/EmptyState';

interface VinMatch {
  dotNumber: string;
  legalName: string;
  status: string;
  matchedVins: string[];
  matchCount: number;
  percentageOfFleet: number;
}

interface ContactMatches {
  dotNumber: number;
  currentCarrier: {
    legalName: string;
  };
  summary: {
    vinMatches: number;
    vinMatchedCompaniesCount: number;
    vinMatchedVinsCount: number;
    totalVinCount: number;
  };
  matches: {
    vins: VinMatch[];
  };
}

const VinMatchesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dotNumber = searchParams.get('dotNumber');

  const [loading, setLoading] = useState(!!dotNumber);
  const [error, setError] = useState<string | null>(null);
  const [contactMatches, setContactMatches] = useState<ContactMatches | null>(null);
  const [timelineVin, setTimelineVin] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'count' | 'percentage'>('count');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (dotNumber) {
      fetchContactMatches();
    }
  }, [dotNumber]);
  const fetchContactMatches = async () => {
    if (!dotNumber) return;

    setLoading(true);
    setError(null);

    try {
      const response = await carrierApi.getContactMatches(parseInt(dotNumber));
      setContactMatches(response as unknown as ContactMatches);
    } catch (err) {
      console.error('Failed to fetch contact matches:', err);
      setError('Failed to load VIN matches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCardExpansion = (dotNumber: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(dotNumber)) {
      newExpanded.delete(dotNumber);
    } else {
      newExpanded.add(dotNumber);
    }
    setExpandedCards(newExpanded);
  };

  const filteredMatches = contactMatches?.matches.vins
    ?.filter(match => {
      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!match.legalName?.toLowerCase().includes(term) &&
            !match.dotNumber?.toLowerCase().includes(term) &&
            !match.matchedVins?.some(vin => vin.toLowerCase().includes(term))) {
          return false;
        }
      }

      // Filter by status
      if (filterStatus === 'active' && match.status !== 'A') return false;
      if (filterStatus === 'inactive' && match.status === 'A') return false;

      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'count') {
        return b.matchCount - a.matchCount;
      } else {
        return b.percentageOfFleet - a.percentageOfFleet;
      }
    });

  const exportToCSV = () => {
    if (!contactMatches?.matches.vins) return;

    const csvContent = [
      ['DOT Number', 'Company Name', 'Status', 'VINs Matched', 'Percentage of Fleet', 'Matched VINs'],
      ...contactMatches.matches.vins.map(match => [
        match.dotNumber,
        match.legalName,
        match.status === 'A' ? 'Active' : 'Inactive',
        match.matchCount,
        `${match.percentageOfFleet}%`,
        match.matchedVins.join('; ')
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vin_matches_${dotNumber}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Show empty state when no DOT number is provided
  if (!dotNumber) {
    return (
      <div className="container-fluid">
        <EmptyState
          icon={Search}
          title="No Carrier Selected"
          description="Please access this page from a carrier's profile to view VIN matches."
          action={{
            label: "Go to Dashboard",
            onClick: () => navigate('/dashboard')
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <EmptyState
          title="Error Loading Data"
          description={error}
          icon={AlertTriangle}
        />
      </div>
    );
  }

  if (!contactMatches) {
    return (
      <div className="container-fluid">
        <EmptyState
          title="No Data Available"
          description="Unable to load VIN match data"
          icon={AlertTriangle}
        />
      </div>
    );
  }

  const totalVinsMatched = contactMatches.summary.vinMatchedVinsCount ||
    contactMatches.matches.vins?.reduce((total, match) => total + match.matchCount, 0) || 0;

  return (
    <div className="container-fluid" style={{ padding: 'var(--space-lg)' }}>
      {/* Header */}
      <div style={{
        marginBottom: 'var(--space-xl)',
        padding: 'var(--space-lg)',
        background: 'white',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
          <button
            onClick={() => {
              if (dotNumber) {
                // Navigate to the carrier dashboard with the specific DOT number
                navigate(`/dashboard?dot=${dotNumber}`);
              } else {
                navigate('/dashboard');
              }
            }}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
          >
            <ArrowLeft size={18} />
            Back to Carrier
          </button>
          <div style={{ flex: 1 }}>
            <h1 className="heading-1" style={{ marginBottom: 'var(--space-xs)' }}>
              VIN Matches Analysis
            </h1>
            <div style={{ fontSize: '1.125rem', color: 'var(--color-gray-600)' }}>
              {contactMatches.currentCarrier.legalName} (DOT {dotNumber})
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
          <div className="card-modern-compact" style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-primary)' }}>
              {totalVinsMatched}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginTop: 'var(--space-xs)' }}>
              Total VINs Matched
            </div>
          </div>
          <div className="card-modern-compact" style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-warning)' }}>
              {contactMatches.summary.vinMatchedCompaniesCount || contactMatches.matches.vins?.length || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginTop: 'var(--space-xs)' }}>
              Companies with Matches
            </div>
          </div>
          <div className="card-modern-compact" style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-success)' }}>
              {contactMatches.summary.totalVinCount}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginTop: 'var(--space-xs)' }}>
              Total Fleet Size
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{
        marginBottom: 'var(--space-lg)',
        padding: 'var(--space-md)',
        background: 'white',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Search by company name, DOT, or VIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem 1rem 0.875rem 40px',
                border: '1.5px solid var(--color-gray-300)',
                borderRadius: 'var(--radius-lg)',
                fontSize: '1rem',
                transition: 'all 0.15s ease',
                outline: 'none'
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--color-gray-300)'; }}
            />
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'count' | 'percentage')}
            style={{
              minWidth: '180px',
              padding: '0.875rem 1rem',
              border: '1.5px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              fontSize: '1rem',
              transition: 'all 0.15s ease',
              outline: 'none',
              cursor: 'pointer',
              background: 'white'
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--color-gray-300)'; }}
          >
            <option value="count">Sort by VIN Count</option>
            <option value="percentage">Sort by % of Fleet</option>
          </select>

          {/* Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
            style={{
              minWidth: '150px',
              padding: '0.875rem 1rem',
              border: '1.5px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              fontSize: '1rem',
              transition: 'all 0.15s ease',
              outline: 'none',
              cursor: 'pointer',
              background: 'white'
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--color-gray-300)'; }}
          >
            <option value="all">All Carriers</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {/* Export */}
          <button
            onClick={exportToCSV}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Results */}
      <div style={{
        background: 'white',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: 'var(--space-lg)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
          <Hash size={24} />
          <h2 className="heading-2">
            {filteredMatches?.length || 0} Companies Found
          </h2>
        </div>

        {filteredMatches && filteredMatches.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {filteredMatches.map((match, idx) => {
              const isExpanded = expandedCards.has(match.dotNumber);
              const showAllVins = isExpanded || match.matchedVins.length <= 10;
              const displayVins = showAllVins ? match.matchedVins : match.matchedVins.slice(0, 10);

              return (
                <div
                  key={idx}
                  className="card-modern-compact"
                  style={{
                    border: '1px solid var(--color-gray-200)',
                    padding: 'var(--space-lg)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 'var(--space-md)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                        <Building size={20} />
                        <span
                          style={{
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            color: 'var(--color-primary)'
                          }}
                          onClick={() => navigate(`/dashboard?dot=${match.dotNumber}`)}
                          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {match.legalName}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-md)', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                          DOT: {match.dotNumber}
                        </span>
                        <span className={`badge-modern ${match.status === 'A' ? 'badge-success' : 'badge-danger'}`}>
                          {match.status === 'A' ? 'Active' : 'Inactive'}
                        </span>
                        <span className="badge-modern badge-warning">
                          {match.matchCount} VINs matched
                        </span>
                        <span className="badge-modern badge-info">
                          {match.percentageOfFleet}% of fleet
                        </span>
                      </div>

                      <div style={{
                        background: '#f8fafc',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid #e2e8f0',
                        padding: 'var(--space-md)'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'var(--space-xs)',
                          maxHeight: isExpanded ? 'none' : '150px',
                          overflow: isExpanded ? 'visible' : 'hidden'
                        }}>
                          {displayVins.map((vin: string) => (
                            <code
                              key={vin}
                              style={{
                                fontSize: '0.8125rem',
                                background: '#dbeafe',
                                padding: '0.25rem 0.5rem',
                                borderRadius: 'var(--radius-sm)',
                                fontFamily: 'monospace',
                                cursor: 'pointer',
                                border: '1px solid #bfdbfe',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTimelineVin(vin);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#3b82f6';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.borderColor = '#2563eb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#dbeafe';
                                e.currentTarget.style.color = 'inherit';
                                e.currentTarget.style.borderColor = '#bfdbfe';
                              }}
                            >
                              {vin}
                            </code>
                          ))}
                        </div>

                        {match.matchedVins.length > 10 && (
                          <button
                            onClick={() => toggleCardExpansion(match.dotNumber)}
                            className="btn-secondary"
                            style={{
                              marginTop: 'var(--space-sm)',
                              fontSize: '0.875rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-xs)'
                            }}
                          >
                            {isExpanded ? (
                              <>Show Less <ChevronUp size={16} /></>
                            ) : (
                              <>Show All {match.matchedVins.length} VINs <ChevronDown size={16} /></>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-xl)',
            color: 'var(--color-gray-500)'
          }}>
            <AlertTriangle size={48} style={{ marginBottom: 'var(--space-md)', opacity: 0.5 }} />
            <div style={{ fontSize: '1.125rem' }}>No matches found</div>
            <div style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>
              Try adjusting your search criteria
            </div>
          </div>
        )}
      </div>

      {/* VIN Detail Modal */}
      {timelineVin && (
        <VINDetailModal
          vin={timelineVin}
          onClose={() => setTimelineVin(null)}
        />
      )}
    </div>
  );
};

export default VinMatchesPage;